import { useEffect, useState, type CSSProperties } from "react";

import { createThemeTokens, DEFAULT_THEME_COLOR, themeCssVariables } from "../shared/theme";
import { SiteFooter } from "./components/SiteFooter";
import { SiteHeader } from "./components/SiteHeader";
import { usePublicState } from "./hooks/usePublicState";
import { getPublicConfig } from "./lib/api";
import { AdminPage } from "./pages/AdminPage";
import { HistoryPage } from "./pages/HistoryPage";
import { HomePage } from "./pages/HomePage";
import { LegalPage } from "./pages/LegalPage";
import { PressPage } from "./pages/PressPage";
import { SuccessPage } from "./pages/SuccessPage";

type PublicStateController = ReturnType<typeof usePublicState>;

function route(publicState: PublicStateController) {
  const path = window.location.pathname.replace(/\/+$/u, "") || "/";
  if (path === "/") return <HomePage {...publicState} />;
  if (path === "/history") return <HistoryPage />;
  if (path === "/success") return <SuccessPage />;
  if (path === "/admin") return <AdminPage />;
  const press = /^\/press\/(\d+)$/u.exec(path);
  if (press?.[1]) return <PressPage pressNumber={Number(press[1])} />;
  const legal = new Set([
    "rules",
    "faq",
    "terms",
    "privacy",
    "refunds",
    "acceptable-use",
    "security",
  ]);
  const page = path.slice(1);
  if (legal.has(page)) return <LegalPage page={page} />;
  return (
    <main className="centered-page">
      <h1>NOTHING TO PRESS HERE.</h1>
      <a className="primary-link" href="/">
        RETURN HOME
      </a>
    </main>
  );
}

export function App() {
  const [testMode, setTestMode] = useState(false);
  const publicState = usePublicState();
  useEffect(() => {
    void getPublicConfig()
      .then((config) => setTestMode(config.appEnvironment !== "production"))
      .catch(() => setTestMode(false));
  }, []);
  const activeThemeColor = publicState.state?.currentHolder?.themeColor ?? DEFAULT_THEME_COLOR;
  const themeStyle = themeCssVariables(activeThemeColor) as CSSProperties;

  useEffect(() => {
    const tokens = createThemeTokens(activeThemeColor);
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    themeMeta?.setAttribute("content", tokens.color);
    document.documentElement.style.backgroundColor = tokens.background;
    document.body.style.backgroundColor = tokens.background;
    if (favicon) {
      const icon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="10" fill="${tokens.background}"/><circle cx="32" cy="34" r="21" fill="${tokens.dark}" stroke="${tokens.readable}" stroke-width="3"/><circle cx="32" cy="30" r="17" fill="${tokens.color}"/></svg>`;
      favicon.href = `data:image/svg+xml,${encodeURIComponent(icon)}`;
    }
  }, [activeThemeColor]);

  return (
    <div className="app-shell" data-theme-color={activeThemeColor} style={themeStyle}>
      <SiteHeader testMode={testMode} />
      {route(publicState)}
      <SiteFooter />
    </div>
  );
}
